import type {TimelineEvent, TimelineSummary, CommitTimelineEvent} from '../../routes/route-payload-types'

export type EventGroup = {
  label: string
  type: string
  count: number
  events: TimelineEvent[]
  link?: string
}

/** Format a user list from events, showing first 2 users and "X others" if more */
export function formatUserList(events: TimelineEvent[], maxUsers: number = 2): string {
  // Extract unique users by login
  const uniqueUsers = Array.from(new Map(events.map(event => [event.user.login, event.user])).values())

  if (uniqueUsers.length === 0) {
    return ''
  }

  // Show first N users
  const displayUsers = uniqueUsers.slice(0, maxUsers)
  const remainingCount = uniqueUsers.length - displayUsers.length

  let userList = displayUsers.map(u => u.login).join(', ')

  if (remainingCount > 0) {
    userList += `, and ${remainingCount} ${remainingCount === 1 ? 'other' : 'others'}`
  }

  return ` by ${userList}`
}

/** Get the verb for the event type */
export function getEventVerb(eventType: string): string {
  switch (eventType) {
    case 'approval':
      return 'approved by'
    case 'changes-requested':
      return 'requested changes by'
    case 'review':
      return 'reviewed by'
    case 'review-comment':
      return 'commented on code by'
    case 'comment':
      return 'commented by'
    case 'commit':
      return 'committed by'
    case 'closed':
      return 'closed by'
    case 'reopened':
      return 'reopened by'
    case 'marked-as-draft':
      return 'marked as draft by'
    case 'ready-for-review':
      return 'marked ready for review by'
    case 'labeled':
      return 'labeled by'
    case 'review-requested':
      return 'requested review by'
    case 'review-dismissed':
      return 'dismissed review by'
    case 'force-pushed':
      return 'force pushed by'
    case 'merged':
      return 'merged by'
    case 'base-changed':
      return 'changed base branch by'
    case 'base-change-failed':
      return 'automatic base change failed by'
    default:
      return 'by'
  }
}

/** Build list of event groups to display */
export function buildEventGroups(summary: TimelineSummary, sinceTimestamp: string, changesUrl: string): EventGroup[] {
  const items: EventGroup[] = []

  if (summary.approvals.events.length > 0) {
    items.push({
      label: `${summary.approvals.events.length} ${summary.approvals.events.length === 1 ? 'approval' : 'approvals'}${formatUserList(summary.approvals.events)}`,
      type: 'approval',
      count: summary.approvals.events.length,
      events: summary.approvals.events,
    })
  }

  if (summary.changesRequested.events.length > 0) {
    items.push({
      label: `${summary.changesRequested.events.length} requested ${summary.changesRequested.events.length === 1 ? 'change' : 'changes'}${formatUserList(summary.changesRequested.events)}`,
      type: 'changes-requested',
      count: summary.changesRequested.events.length,
      events: summary.changesRequested.events,
    })
  }

  if (summary.comments.events.length > 0) {
    items.push({
      label: `${summary.comments.events.length} ${summary.comments.events.length === 1 ? 'review' : 'reviews'}${formatUserList(summary.comments.events)}`,
      type: 'review',
      count: summary.comments.events.length,
      events: summary.comments.events,
    })
  }

  if (summary.reviewComments.events.length > 0) {
    // Filter by date using the since timestamp
    const sinceDate = new Date(sinceTimestamp)
    const dateString = sinceDate.toISOString().split('T')[0] // YYYY-MM-DD format
    const filterText = `after:${dateString}`

    items.push({
      label: `${summary.reviewComments.events.length} review ${summary.reviewComments.events.length === 1 ? 'comment' : 'comments'}${formatUserList(summary.reviewComments.events)}`,
      type: 'review-comment',
      count: summary.reviewComments.events.length,
      events: summary.reviewComments.events,
      link: `${changesUrl}?filterText=${encodeURIComponent(filterText)}`,
    })
  }

  if (summary.issueComments.events.length > 0) {
    items.push({
      label: `${summary.issueComments.events.length} ${summary.issueComments.events.length === 1 ? 'comment' : 'comments'}${formatUserList(summary.issueComments.events)}`,
      type: 'comment',
      count: summary.issueComments.events.length,
      events: summary.issueComments.events,
    })
  }

  if (summary.commits.events.length > 0) {
    let commitRangeLink: string | undefined

    // Build link to Files tab scoped to commit range
    // Use baseSha from first commit and sha from last commit
    const firstCommit = summary.commits.events[0] as CommitTimelineEvent
    const lastCommit = summary.commits.events[summary.commits.events.length - 1] as CommitTimelineEvent

    if (firstCommit?.additionalInfo.baseSha && lastCommit?.additionalInfo.sha) {
      commitRangeLink = `${changesUrl}/${firstCommit.additionalInfo.baseSha}..${lastCommit.additionalInfo.sha}`
    }

    items.push({
      label: `${summary.commits.events.length} ${summary.commits.events.length === 1 ? 'commit' : 'commits'}${formatUserList(summary.commits.events)}`,
      type: 'commit',
      count: summary.commits.events.length,
      events: summary.commits.events,
      link: commitRangeLink,
    })
  }

  if (summary.closed.events.length > 0) {
    items.push({
      label: `Closed${formatUserList(summary.closed.events)}`,
      type: 'closed',
      count: summary.closed.events.length,
      events: summary.closed.events,
    })
  }

  if (summary.reopened.events.length > 0) {
    items.push({
      label: `Reopened${formatUserList(summary.reopened.events)}`,
      type: 'reopened',
      count: summary.reopened.events.length,
      events: summary.reopened.events,
    })
  }

  if (summary.markedAsDraft.events.length > 0) {
    items.push({
      label: `Marked as draft${formatUserList(summary.markedAsDraft.events)}`,
      type: 'marked-as-draft',
      count: summary.markedAsDraft.events.length,
      events: summary.markedAsDraft.events,
    })
  }

  if (summary.readyForReview.events.length > 0) {
    items.push({
      label: `Ready for review${formatUserList(summary.readyForReview.events)}`,
      type: 'ready-for-review',
      count: summary.readyForReview.events.length,
      events: summary.readyForReview.events,
    })
  }

  if (summary.labeled.events.length > 0) {
    items.push({
      label: `${summary.labeled.events.length} label ${summary.labeled.events.length === 1 ? 'change' : 'changes'}${formatUserList(summary.labeled.events)}`,
      type: 'labeled',
      count: summary.labeled.events.length,
      events: summary.labeled.events,
    })
  }

  if (summary.reviewRequested.events.length > 0) {
    items.push({
      label: `${summary.reviewRequested.events.length} review ${summary.reviewRequested.events.length === 1 ? 'request change' : 'request changes'}${formatUserList(summary.reviewRequested.events)}`,
      type: 'review-requested',
      count: summary.reviewRequested.events.length,
      events: summary.reviewRequested.events,
    })
  }

  if (summary.reviewDismissed.events.length > 0) {
    items.push({
      label: `${summary.reviewDismissed.events.length} review${summary.reviewDismissed.events.length === 1 ? '' : 's'} dismissed${formatUserList(summary.reviewDismissed.events)}`,
      type: 'review-dismissed',
      count: summary.reviewDismissed.events.length,
      events: summary.reviewDismissed.events,
    })
  }

  if (summary.forcePushed.events.length > 0) {
    items.push({
      label: `Force pushed ${summary.forcePushed.events.length} ${summary.forcePushed.events.length === 1 ? 'time' : 'times'}${formatUserList(summary.forcePushed.events)}`,
      type: 'force-pushed',
      count: summary.forcePushed.events.length,
      events: summary.forcePushed.events,
    })
  }

  if (summary.merged.events.length > 0) {
    items.push({
      label: `Merged${formatUserList(summary.merged.events)}`,
      type: 'merged',
      count: summary.merged.events.length,
      events: summary.merged.events,
    })
  }

  if (summary.baseChanged.events.length > 0) {
    items.push({
      label: `Base branch changed${formatUserList(summary.baseChanged.events)}`,
      type: 'base-changed',
      count: summary.baseChanged.events.length,
      events: summary.baseChanged.events,
    })
  }

  if (summary.baseChangeFailed.events.length > 0) {
    items.push({
      label: `Automatic base change failed${formatUserList(summary.baseChangeFailed.events)}`,
      type: 'base-change-failed',
      count: summary.baseChangeFailed.events.length,
      events: summary.baseChangeFailed.events,
    })
  }

  return items
}
